import React, { useEffect, useState } from 'react';
import Drawer from '../../Common/Drawer';
import RichTextField from './RichTextField';
import DrawerTaskTree from './DrawerTaskTree';
import CreateWorkItemForm from './CreateWorkItemForm';
import Discussion from './Discussion';
import FieldEditingIndicator from './FieldEditingIndicator';
import * as live from '../../../api/liveClient';
import {
  takeTaskOwnership, updateTaskDeliveryMeta, addTaskComment,
  setTaskAssignee, readServerError,
} from '../../../api/deliveryApi';

// UDRAW-S2 §2 — shared ADO frame for task/phase drawers.
// Renders the same title/strip/toolbelt/2-column/tree shell that
// WorkItemDrawer's normal mode uses (see WorkItemDrawer.js §UDRAW-S1),
// wrapped around task-shaped content. The old QLOG-S2 read-only info
// drawer and the QLOG-S2 quickLog drawer both delegate to THIS
// component so the layout is one implementation.
//
// Two writable fields:
//   - Status:  <select> (NotStarted / InProgress / Completed), edit
//              rights = PM OR current owner; readers see a chip.
//   - Notes:   <RichTextField/> (2000-char lim), same rights.
// One write action:
//   - Take ownership: link in the Owner cell — visible whenever the
//     caller is NOT the current owner AND is PM OR a project team
//     member. Free-take semantics (server allows takeover of a busy
//     task per spec §1.1).
// One flush:
//   - Toolbelt Save → single PATCH delivery-meta with any dirty
//     Status/Notes; Cancel reverts to the loaded values. Non-editors
//     see no toolbelt.
//
// Props (see WorkItemDrawer for the two call sites):
//   open, onClose
//   projectId
//   task            — DeliveryTaskDto (must include id, name, status,
//                     notes, ownerEmployeeId, ownerFullName)
//   team, caller    — same shape DeliveryPage feeds WorkItemDrawer
//   mode            — 'phase' | 'summary' | 'task-with-wis' |
//                     'milestone' | 'quickLog'
//   banner          — { tone: 'info'|'warn'|'good', text } or null
//   childRows       — [{ id, title, subtitle?, trailing?, onClick?,
//                        testId? }] rendered in the left column
//                     between the banner and the Notes editor
//   childRowsTitle  — heading above childRows (default 'Rows')
//   cards           — [{ label, value, tone? }] shown in the right
//                     column (Продадено / Изработено / Остатък etc.)
//   rightBottomNode — extra right-column node below `cards`
//                     (QuickLogInner slots the Role picker +
//                     HoursBlock here)
//   treeData        — pre-flattened sub-tree (DrawerTaskTree)
//   onTreeNavigate  — (node) => void; DeliveryPage routes it back
//                     through its normal row-click dispatch
//   onTaskChanged   — (freshPartial) => void; called after a
//                     successful Save or Take-ownership. Payload:
//                     { id, status?, notes?, ownerEmployeeId?,
//                       ownerFullName? }.

const KIND_LABEL = {
  phase: 'PHASE',
  summary: 'TASK',
  'task-with-wis': 'TASK',
  milestone: 'MILESTONE',
  quickLog: 'TASK',
};

const STATE_DOT = (s) =>
  s === 'InProgress' ? 'd-act' :
  s === 'Completed' ? 'd-cls' : 'd-new';

const initials = (n) => (n || '?')
  .split(/\s+/).slice(0, 2)
  .map((s) => s.charAt(0).toUpperCase()).join('');

export default function TaskDrawerForm({
  open, onClose,
  projectId, task, team, caller,
  mode,
  banner = null,
  childRows = null,
  childRowsTitle = 'Rows',
  cards = null,
  rightBottomNode = null,
  treeData = null,
  onTreeNavigate = null,
  onTaskChanged = null,
  // quickLog uses this to inject its "Role (for logging)" picker into
  // the strip's Role cell. When null the cell falls back to "—" (or
  // "in the work items" for task-with-wis) per mockup.
  roleCellNode = null,
  // UDRAW-S3 §2.2 — the drawer becomes the WI-create surface. The
  // toolbelt "+ New work item" opens CreateWorkItemForm inline for
  // costed leaf / task-with-wis rows. `isBillable` gates the role
  // picker inside the form; `onWorkItemCreated` lets the caller
  // refresh the board and lands the fresh id in the banner. When
  // `autoOpenCreate` is truthy the form opens on mount (used by the
  // "+ work item" / "Add work item" entry points that replace the
  // pre-UDRAW-S3 ghost row).
  isBillable = true,
  onWorkItemCreated = null,
  autoOpenCreate = false,
  // UDRAW-S4 §2.1 — parent receives the fresh comment DTO so the
  // task's `comments` array stays in sync without a full reload.
  onCommentAdded = null,
  // ASGN-S1 §3.2 — the "🕒 Log hours" link in the Assignee context
  // strip calls this. Parent (DeliveryPage) wires it to whatever
  // opens QuickLog for this task. When absent, the link is hidden
  // (the strip's info message still renders for context).
  onOpenQuickLog = null,
  // UDRAW-S5.1 §2.2.4 — DeliveryPage-supplied peer-editing map,
  // filtered to THIS task's anchor (t-{id}). Keys are the raw
  // fieldKey (e.g. 'notes', 'state', 'discussion'); values
  // { employeeId, fullName }.
  liveEditing = null,
}) {
  const isMilestone = mode === 'milestone';
  const isPm = !!caller?.isPm;
  const meId = caller?.employeeId ?? null;
  const meIsOwner = !!task?.ownerEmployeeId && task.ownerEmployeeId === meId;
  const isTeam = !!(team || []).find((m) => m.employeeId === meId);
  const canEdit = !isMilestone && (isPm || meIsOwner);
  const canTakeOwnership = !isMilestone && !meIsOwner && (isPm || isTeam);
  // ASGN-S1 §3.1 — the Assignee picker is offered to PM OR current
  // Owner only. Everyone else sees the read-only cell.
  const canManageAssignee = !isMilestone && (isPm || meIsOwner);
  // Assignee context (§3.2). meIsAssignee drives the "🕒 Log hours"
  // affordance; hasAssignee gates the "No assignee yet" info bar.
  const hasAssignee = !!task?.assigneeEmployeeId;
  const meIsAssignee = hasAssignee && task.assigneeEmployeeId === meId;
  // WBS-RETIRE — `isWbs` was always false in production post-slice; removed.

  // Local drafts; Save flushes both in a single PATCH.
  const [statusDraft, setStatusDraft] = useState(task?.status || 'NotStarted');
  const [notesDraft, setNotesDraft] = useState(task?.notes || '');
  const [dirty, setDirty] = useState({ status: false, notes: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);

  // UDRAW-S3 §2.2 — the toolbelt's "+ New work item" flips this on;
  // the body swaps to CreateWorkItemForm. On success the flag flips
  // back and `justCreated` shows the green Open-→ banner.
  const [createOpen, setCreateOpen] = useState(false);
  const [justCreated, setJustCreated] = useState(null);
  useEffect(() => {
    // Auto-open (used by "+ work item" entry points that opened this
    // drawer specifically to create). Runs once per task change so a
    // re-render doesn't spuriously reopen a closed form.
    if (autoOpenCreate) setCreateOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id]);
  // WBS-RETIRE — the `task.taskMode !== 'WBS'` guard is dead.
  const canCreateWi = !isMilestone && !!task
    && (isPm || meIsOwner || isTeam);

  // Re-seed when the underlying task changes (parent refetched or the
  // drawer opens a different row).
  useEffect(() => {
    setStatusDraft(task?.status || 'NotStarted');
    setNotesDraft(task?.notes || '');
    setDirty({ status: false, notes: false });
    setError('');
  }, [task?.id, task?.status, task?.notes]);

  const onStatusChange = (v) => {
    setStatusDraft(v);
    setDirty((d) => d.status && v === (task?.status || 'NotStarted') ? { ...d, status: false } : { ...d, status: true });
  };

  // RichTextField calls onSave with the sanitized HTML when the user
  // clicks its inline Save — we STAGE it (no request) so the toolbelt
  // Save can flush status+notes together per spec §2.1.3.
  const stageNotes = async (html) => {
    setNotesDraft(html || '');
    setDirty((d) => ({ ...d, notes: (html || '') !== (task?.notes || '') }));
  };

  const doSave = async () => {
    if (!canEdit) return;
    const body = {};
    if (dirty.status) body.status = statusDraft;
    if (dirty.notes) body.notes = notesDraft;
    if (Object.keys(body).length === 0) return;
    setSaving(true); setError('');
    try {
      const res = await updateTaskDeliveryMeta(projectId, task.id, body);
      onTaskChanged && onTaskChanged({
        id: task.id,
        status: res?.status ?? statusDraft,
        notes: res?.notes ?? notesDraft,
      });
      setDirty({ status: false, notes: false });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
    } catch (e) {
      setError(readServerError(e));
    } finally {
      setSaving(false);
    }
  };

  const doCancel = () => {
    setStatusDraft(task?.status || 'NotStarted');
    setNotesDraft(task?.notes || '');
    setDirty({ status: false, notes: false });
    setError('');
  };

  const doTake = async () => {
    setSaving(true); setError('');
    try {
      const res = await takeTaskOwnership(projectId, task.id);
      onTaskChanged && onTaskChanged({
        id: task.id,
        ownerEmployeeId: res?.ownerEmployeeId ?? meId,
        ownerFullName: res?.ownerFullName ?? null,
      });
    } catch (e) {
      setError(readServerError(e));
    } finally {
      setSaving(false);
    }
  };

  // ASGN-S1 §3.1 — PM / Owner picks a team member as the task's
  // Assignee (or clears with the "— unassigned —" option). Optimistic
  // path: on success we flip the drawer's Assignee cell via
  // onTaskChanged; server returns the fresh full name.
  const doChangeAssignee = async (raw) => {
    const nextId = (raw === '' || raw == null) ? null : Number(raw);
    setSaving(true); setError('');
    try {
      const res = await setTaskAssignee(projectId, task.id, nextId);
      onTaskChanged && onTaskChanged({
        id: task.id,
        assigneeEmployeeId: res?.assigneeEmployeeId ?? nextId,
        assigneeFullName: res?.assigneeFullName ?? null,
      });
    } catch (e) {
      setError(readServerError(e));
    } finally {
      setSaving(false);
    }
  };

  const codeLabel = KIND_LABEL[mode] || 'TASK';
  const takeLinkTestId = task?.ownerEmployeeId
    ? 'task-drawer-take-over'
    : 'task-drawer-take-ownership';
  const takeLinkLabel = task?.ownerEmployeeId ? 'Take over' : 'Take ownership';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width="xl"
      title={
        <span
          data-testid="task-drawer-title"
          className="pdp"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <span style={{ color: '#4a90d9', fontWeight: 600, fontSize: 12 }}>{codeLabel}</span>
          <span
            data-testid="task-drawer-kind"
            style={{
              fontSize: 10, color: '#0b5394', background: '#e3ecf7',
              border: '1px solid #b7cbe7', borderRadius: 3,
              padding: '1px 7px', letterSpacing: '.4px', fontWeight: 700,
            }}
          >{(mode || '').toUpperCase()}</span>
        </span>
      }
      testId="wi-drawer"
    >
      <div
        className="pdp dlv-drawer"
        data-dlv-drawer
        // The wrapper testid uses the pre-UDRAW-S2 naming so the QLOG-S2
        // test suite (and the QLOG-S2.3 fence that asserts
        // `data-dlv-drawer` on the wrapper) keep passing; new UDRAW-S2
        // tests use the inner-control testids below (task-drawer-*).
        data-testid={mode === 'quickLog'
          ? 'wi-drawer-quicklog'
          : `wi-drawer-info-${mode || 'task'}`}
        data-drawer-mode={`task-drawer-${mode || 'task'}`}
        style={{ padding: 12 }}
      >
        {/* Title (task name — always read-only, name is managed in the plan) */}
        <div className="wi-drawer-title-row" data-testid="wi-drawer-title-row">
          <div
            className="wi-drawer-title-ro"
            data-testid="task-drawer-title-ro"
            title="Task name is managed in the plan"
          >
            {/* QLOG-S2 compat: earlier suites read the task name via
                wi-drawer-info-h. */}
            <span data-testid="wi-drawer-info-h">{task?.name || ''}</span>
          </div>
        </div>

        {/* Strip: State / Owner / Role (Role is caller-supplied for
            quickLog; a plain '—' otherwise per mockup). */}
        <div className="wi-drawer-strip" data-testid="wi-drawer-strip">
          <div className="cell">
            <div className="l">
              State
              <FieldEditingIndicator fieldKey="state" peer={liveEditing?.state} />
            </div>
            {canEdit ? (
              <select
                data-testid="task-drawer-status"
                value={statusDraft}
                onChange={(e) => onStatusChange(e.target.value)}
                onFocus={() => live.startEditing({ projectId, taskId: task?.id, fieldKey: 'state' })}
                onBlur={() => live.stopEditing({ projectId, taskId: task?.id, fieldKey: 'state' })}
                disabled={saving}
              >
                <option value="NotStarted">Not started</option>
                <option value="InProgress">In progress</option>
                <option value="Completed">Completed</option>
              </select>
            ) : (
              <div className="pv" data-testid="task-drawer-status-ro">
                <span className={`dot ${STATE_DOT(statusDraft)}`} />
                <span>{statusDraft === 'InProgress' ? 'In progress'
                  : statusDraft === 'Completed' ? 'Completed' : 'Not started'}</span>
              </div>
            )}
          </div>

          <div className="cell">
            <div className="l">Owner</div>
            <div className="pv" data-testid="task-drawer-owner">
              {task?.ownerFullName ? (
                <>
                  <span className="avat">{initials(task.ownerFullName)}</span>
                  <span>{task.ownerFullName}</span>
                </>
              ) : (
                <span className="muted">— unassigned —</span>
              )}
              {canTakeOwnership && (
                <button
                  type="button"
                  className="linkbtn"
                  data-testid={takeLinkTestId}
                  onClick={doTake}
                  disabled={saving}
                >{takeLinkLabel}</button>
              )}
            </div>
          </div>

          {/* ASGN-S1 §3.1 — Assignee cell. Picker (<select>) when
              caller is PM or current Owner; read-only chip otherwise.
              Skipped for milestones (same as State/Owner on milestone
              rows — see canEdit / canManageAssignee gates). */}
          {!isMilestone && (
            <div className="cell">
              <div className="l">Assignee</div>
              {canManageAssignee ? (
                <select
                  data-testid="task-drawer-assignee-select"
                  value={task?.assigneeEmployeeId ?? ''}
                  onChange={(e) => doChangeAssignee(e.target.value)}
                  disabled={saving}
                >
                  <option value="">— unassigned —</option>
                  {(team || []).map((m) => (
                    <option key={m.employeeId} value={m.employeeId}>{m.fullName}</option>
                  ))}
                </select>
              ) : (
                <div className="pv" data-testid="task-drawer-assignee">
                  {task?.assigneeFullName ? (
                    <>
                      <span className="avat">{initials(task.assigneeFullName)}</span>
                      <span>{task.assigneeFullName}</span>
                    </>
                  ) : (
                    <span className="muted">— unassigned —</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Role — quickLog drops its own <select> in via
              roleCellNode; the default cell shows "—" (or
              "in the work items" for task-with-wis) per mockup. */}
          <div className="cell">
            <div className="l">Role</div>
            {roleCellNode ? (
              roleCellNode
            ) : (
              <div className="pv muted" data-testid="task-drawer-role-ro">
                {mode === 'task-with-wis' ? 'in the work items' : '—'}
              </div>
            )}
          </div>
        </div>

        {/* Toolbelt — Save + Cancel + saved flash (writable rows) +
            "+ New work item" (any team member on a costed task). While
            the create form is open the Save/Cancel/Create buttons live
            inside the form; the toolbelt only shows the row-level
            controls the drawer still owns. */}
        {(canEdit || canCreateWi) && !createOpen && (
          <div className="wi-drawer-toolbelt" data-testid="task-drawer-toolbelt">
            {canEdit && (
              <>
                <button
                  type="button"
                  className="pri"
                  data-testid="task-drawer-save"
                  onClick={doSave}
                  disabled={saving || (!dirty.status && !dirty.notes)}
                >{saving ? 'Saving…' : '💾 Save'}</button>
                <button
                  type="button"
                  data-testid="task-drawer-cancel"
                  onClick={doCancel}
                  disabled={saving || (!dirty.status && !dirty.notes)}
                >Cancel</button>
              </>
            )}
            {canCreateWi && (
              <>
                {canEdit && <span className="sep" />}
                <button
                  type="button"
                  data-testid="task-drawer-new-wi"
                  onClick={() => { setError(''); setJustCreated(null); setCreateOpen(true); }}
                  disabled={saving}
                >＋ New work item</button>
              </>
            )}
            {savedFlash && (
              <span
                data-testid="task-drawer-saved-note"
                style={{ marginLeft: 8, fontSize: 11, color: '#2a6b2a' }}
              >✓ Saved</span>
            )}
            {error && (
              <span
                data-testid="task-drawer-err"
                style={{ marginLeft: 8, fontSize: 11, color: '#a33' }}
              >{error}</span>
            )}
          </div>
        )}

        {/* Green "Created" banner surfaces after a successful create.
            The Open → link lets the caller jump straight into the new
            WI's drawer without hunting through the tree. */}
        {justCreated && !createOpen && (
          <div
            className="wi-drawer-banner good"
            data-testid="task-drawer-created-banner"
            style={{ margin: '10px 4px 0' }}
          >
            Created WI-{justCreated.id} · {justCreated.title} —
            {' '}
            <button
              type="button"
              className="linkbtn"
              data-testid="task-drawer-created-open"
              style={{ background: 'none', border: 'none', padding: 0, color: '#2f6fed', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => {
                if (typeof onWorkItemCreated === 'function') {
                  onWorkItemCreated(justCreated, { navigate: true });
                }
              }}
            >Open →</button>
          </div>
        )}

        {/* UDRAW-S3 §2.2 — when the "+ New work item" button is armed,
            the body swaps to the create form; Cancel returns here. */}
        {createOpen ? (
          <div
            className="wi-drawer-body-grid single"
            data-testid="wi-drawer-body"
          >
            <div className="colL">
              <CreateWorkItemForm
                projectId={projectId}
                task={task}
                parentWi={null}
                team={team}
                caller={caller}
                isBillable={isBillable}
                onCreated={(dto) => {
                  setCreateOpen(false);
                  setJustCreated({ id: dto?.id, title: dto?.title || '(no title)' });
                  if (typeof onWorkItemCreated === 'function') {
                    onWorkItemCreated(dto, { navigate: false });
                  }
                }}
                onCancel={() => setCreateOpen(false)}
              />
            </div>
          </div>
        ) : (
        <div
          className={`wi-drawer-body-grid${(!cards && !rightBottomNode) ? ' single' : ''}`}
          data-testid="wi-drawer-body"
        >
          <div className="colL">
            {banner && (
              <div
                className={`wi-drawer-banner ${banner.tone || 'info'}`}
                data-testid="task-drawer-banner"
              >
                {/* QLOG-S2 compat alias — earlier suites read the banner
                    text via wi-drawer-info-banner. */}
                <span data-testid="wi-drawer-info-banner">{banner.text}</span>
              </div>
            )}

            {Array.isArray(childRows) && childRows.length > 0 && (
              <div
                data-testid="task-drawer-child-rows"
              >
                <div className="wi-drawer-sec-t">{childRowsTitle}</div>
                {/* QLOG-S2 compat: earlier suites keyed off
                    wi-drawer-info-children AND read row text through it. */}
                <table
                  data-testid="wi-drawer-info-children"
                  style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}
                >
                  <tbody>
                    {childRows.map((ch) => (
                      <tr
                        key={ch.id}
                        data-testid={ch.testId || `task-drawer-child-${ch.id}`}
                        onClick={ch.onClick}
                        style={{ cursor: ch.onClick ? 'pointer' : 'default' }}
                      >
                        <td style={{ padding: '4px 8px', borderBottom: '1px solid #eee' }}>
                          <div>{ch.title}</div>
                          {ch.subtitle && <div style={{ color: '#888', fontSize: 11.5 }}>{ch.subtitle}</div>}
                        </td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', borderBottom: '1px solid #eee', color: '#888', fontSize: 11.5 }}>
                          {ch.trailing || ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!isMilestone && (
              <div>
                <div className="wi-drawer-sec-t">
                  Notes
                  <FieldEditingIndicator fieldKey="notes" peer={liveEditing?.notes} />
                </div>
                <RichTextField
                  testId="task-drawer-notes"
                  value={notesDraft}
                  placeholder={canEdit ? 'Click to add notes…' : '—'}
                  readOnly={!canEdit}
                  maxLength={2000}
                  onSave={stageNotes}
                  onEditingStart={() => live.startEditing({ projectId, taskId: task?.id, fieldKey: 'notes' })}
                  onEditingStop={() => live.stopEditing({ projectId, taskId: task?.id, fieldKey: 'notes' })}
                />
              </div>
            )}
            {/* ASGN-S1 §3.2 — Assignee-aware logging strip. Under
                Notes so it sits next to the writable meta but above
                Discussion (which is about coordination, not hours).
                Skipped for milestones and WBS rows (neither accepts
                hours). Three states — mutually exclusive:
                  • No assignee → grey banner directing PM/Owner to
                    the Assignee cell above.
                  • Caller IS the assignee → neutral banner + a
                    "🕒 Log hours" link (visible only when the
                    parent supplied onOpenQuickLog).
                  • Caller is anyone else → grey banner with the
                    assignee's name (nothing loggable here for them).
                Backend §1.7 enforces the same rule on the QuickLog
                endpoint; this UI just previews the outcome. */}
            {!isMilestone && task?.id != null && (
              <div
                className="wi-drawer-strip"
                data-testid="task-drawer-assignee-strip"
                style={{
                  marginTop: 8, padding: '8px 10px', fontSize: 12,
                  background: !hasAssignee ? '#f6f7f8'
                    : meIsAssignee ? '#eaf7ee' : '#f6f7f8',
                  border: !hasAssignee ? '1px solid #e0e2e5'
                    : meIsAssignee ? '1px solid #bde0c7' : '1px solid #e0e2e5',
                  color: '#333', borderRadius: 3,
                }}
              >
                {!hasAssignee && (
                  <span data-testid="task-drawer-assignee-strip-none">
                    No assignee yet — the task owner or PM can assign someone from the Assignee cell above.
                  </span>
                )}
                {hasAssignee && meIsAssignee && (
                  <>
                    <span>You are the assignee for this task.</span>
                    {typeof onOpenQuickLog === 'function' && (
                      <>
                        {' '}
                        <button
                          type="button"
                          className="linkbtn"
                          data-testid="task-drawer-assignee-strip-log"
                          onClick={() => onOpenQuickLog(task.id)}
                        >🕒 Log hours</button>
                      </>
                    )}
                  </>
                )}
                {hasAssignee && !meIsAssignee && (
                  <span data-testid="task-drawer-assignee-strip-other">
                    Assigned to {task.assigneeFullName}. Only the assignee can log hours here.
                  </span>
                )}
              </div>
            )}

            {/* UDRAW-S4 §2.1 — task-level Discussion sits under Notes
                in every live TaskDrawerForm mode. Same shared component
                as the WI drawer's discussion so list markup + rich
                editor stay one implementation. Skipped for milestone
                (spec §2.1) — the milestone row is out-of-scope.
                UDRAW-S5.1 — editor focus/blur fires fieldEditing. */}
            {!isMilestone && task?.id != null && (
              <>
                <Discussion
                  // UDRAW-S5-A8 / A10 — stable key tied to task.id
                  // so a silent A10 refresh reuses the same
                  // Discussion instance (draft in the DOM survives).
                  // Remounts only when the drawer navigates to a
                  // different task (fresh empty editor — correct).
                  key={`task-${task.id}-discussion`}
                  comments={task?.comments || []}
                  onPost={async (html) => {
                    const res = await addTaskComment(projectId, task.id, html);
                    if (res && res.id != null && typeof onCommentAdded === 'function') {
                      onCommentAdded(task.id, res);
                    }
                  }}
                  testIdPrefix="task-drawer"
                  canPost={isPm || isTeam}
                  onEditorFocus={() => live.startEditing({ projectId, taskId: task.id, fieldKey: 'discussion' })}
                  onEditorBlur={() => live.stopEditing({ projectId, taskId: task.id, fieldKey: 'discussion' })}
                />
                {liveEditing?.discussion && (
                  <FieldEditingIndicator fieldKey="discussion" peer={liveEditing.discussion} />
                )}
              </>
            )}
          </div>

          {(cards || rightBottomNode) && (
            <div className="colR">
              {Array.isArray(cards) && cards.length > 0 && (
                <section
                  data-testid="task-drawer-cards"
                  style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}
                >
                  {cards.map((c, i) => (
                    <div key={i}
                         data-testid={`task-drawer-card-${i}`}
                         style={{
                           border: '1px solid #d0d7e2', borderRadius: 4,
                           padding: '8px 12px', minWidth: 110, background: '#fafbfd',
                         }}>
                      <div style={{ fontSize: 11, color: '#666' }}>{c.label}</div>
                      <div style={{
                        fontSize: 16, fontWeight: 700,
                        color: c.tone === 'over' ? '#c0392b' : c.tone === 'ok' ? '#2a6b2a' : '#333',
                      }}>{c.value}</div>
                    </div>
                  ))}
                </section>
              )}
              {rightBottomNode}
            </div>
          )}
        </div>
        )}

        <DrawerTaskTree
          treeData={treeData}
          onNavigate={onTreeNavigate}
        />
      </div>
    </Drawer>
  );
}
